import { useState } from "react";
import { api, getToken, type Paged } from "../lib/api.js";
import { bytes, dateTime, deviceId } from "../lib/format.js";
import { Drawer, Empty, Notice, Tabs, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

type Kind = "conn" | "file" | "alarm" | "console" | "login" | "recordings";

const TABS: Array<{ id: Kind; label: string }> = [
  { id: "conn", label: "Sessions" },
  { id: "file", label: "File transfers" },
  { id: "login", label: "Sign-ins" },
  { id: "console", label: "Console changes" },
  { id: "alarm", label: "Alarms" },
  { id: "recordings", label: "Recordings" },
];

export default function Audit({ onToggleRail }: PageProps) {
  const [kind, setKind] = useState<Kind>("conn");
  const [page, setPage] = useState(1);
  const [playing, setPlaying] = useState<any>(null);
  const rows = useAsync(
    () => api.get<Paged<any>>(
      // Recordings are their own thing: a machine uploads them while it is
      // still recording, so they are not audit rows.
      kind === "recordings"
        ? `/api/console/recordings?current=${page}&pageSize=100`
        : `/api/console/audit/${kind}?current=${page}&pageSize=100`,
    ),
    [kind, page],
  );

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Activity</h1>
          <span className="topbar__hint">{rows.data?.total ?? 0} records</span>
        </div>
        <div className="topbar__actions">
          <button className="btn btn--sm" onClick={rows.reload}>Refresh</button>
        </div>
      </header>

      <div className="content">
        <Tabs tabs={TABS} active={kind} onChange={(k) => { setKind(k); setPage(1); }} />
        {rows.error ? <Notice>{rows.error}</Notice> : null}
        {kind === "recordings" ? <RecordingSetting /> : null}

        {!rows.loading && !rows.data?.data.length ? (
          <Empty title="Nothing recorded yet" hint="Activity appears here as people connect and make changes." />
        ) : (
          <>
            <div className="panel">
              <div className="tablewrap">
                <table className="data">
                  <thead><tr>{headers(kind).map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(rows.data?.data ?? []).map((r: any) => (
                      <tr key={r.id}>
                        {kind === "recordings" ? recordingCells(r, setPlaying) : cells(kind, r)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="row" style={{ marginTop: "0.75rem" }}>
              <button className="btn btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span className="small faint">Page {page}</span>
              <button
                className="btn btn--sm"
                disabled={(rows.data?.total ?? 0) <= page * 100}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {playing ? (
        <Drawer
          title={`Recording of ${deviceId(playing.device_id || playing.peer_id)}`}
          onClose={() => setPlaying(null)}
          footer={<button className="btn" onClick={() => setPlaying(null)}>Close</button>}
        >
          <div className="stack">
            <video
              controls
              autoPlay
              style={{ width: "100%", background: "#000", borderRadius: "var(--radius)" }}
              src={`/api/console/recordings/${playing.id}/download?token=${
                encodeURIComponent(getToken())}`}
            />
            <div className="muted small">
              {dateTime(playing.started_at)} · {bytes(playing.size)} ·{" "}
              {String(playing.codec).toUpperCase()}
              {playing.complete ? "" : " · still recording"}
            </div>
            {!playing.complete ? (
              <div className="notice">
                This session is still going. The file is complete only once it ends,
                so it may stop short or refuse to seek until then.
              </div>
            ) : null}
          </div>
        </Drawer>
      ) : null}
    </>
  );
}

function headers(kind: Kind): string[] {
  switch (kind) {
    case "conn": return ["When", "Machine", "Person", "Action", "From", "Note"];
    case "file": return ["When", "Machine", "Direction", "Path", "Details"];
    case "login": return ["When", "Person", "Result", "From", "Reason"];
    case "console": return ["When", "Person", "Change", "Target", "From"];
    case "alarm": return ["When", "Machine", "Type", "Details"];
    case "recordings": return ["When", "Machine", "Screen", "Size", "State", ""];
  }
}

/**
 * A recording is watched here rather than downloaded.
 *
 * It is a WebM the browser can already play, and asking someone to save a file
 * to look at one moment of a session is how nobody ever looks.
 */
function recordingCells(r: any, play: (row: any) => void) {
  return [
    <td key="w" className="small nowrap faint">{dateTime(r.started_at)}</td>,
    <td key="d" className="mono">{deviceId(r.device_id || r.peer_id)}</td>,
    <td key="s" className="muted small">
      {r.display > 0 ? `Screen ${r.display + 1}` : "Screen 1"} · {String(r.codec).toUpperCase()}
    </td>,
    <td key="z" className="num muted small">{bytes(r.size)}</td>,
    <td key="c">
      {r.complete
        ? <span className="tag tag--live">Complete</span>
        : <span className="tag tag--warn">Recording</span>}
    </td>,
    <td key="a" className="actions">
      <button className="btn btn--sm" onClick={() => play(r)}>Watch</button>
    </td>,
  ];
}

function cells(kind: Kind, r: any) {
  const when = <td key="w" className="small nowrap faint">{dateTime(r.created_at)}</td>;
  switch (kind) {
    case "conn":
      return [
        when,
        <td key="d" className="mono">{deviceId(r.device_id)}</td>,
        <td key="u">{r.username || <span className="faint">Not signed in</span>}</td>,
        <td key="a"><span className="tag">{r.action}</span></td>,
        <td key="i" className="mono small faint">{r.ip}</td>,
        <td key="n" className="muted small truncate" style={{ maxWidth: 240 }}>{r.note}</td>,
      ];
    case "file":
      return [
        when,
        <td key="d" className="mono">{deviceId(r.device_id)}</td>,
        <td key="t">{r.type === 0 ? "Download" : "Upload"}</td>,
        <td key="p" className="mono small truncate" style={{ maxWidth: 280 }}>{r.path}</td>,
        <td key="i" className="muted small truncate" style={{ maxWidth: 240 }}>{r.info}</td>,
      ];
    case "login":
      return [
        when,
        <td key="u">{r.username}</td>,
        <td key="ok">
          <span className={`tag ${r.ok ? "tag--live" : "tag--deny"}`}>{r.ok ? "Success" : "Refused"}</span>
        </td>,
        <td key="i" className="mono small faint">{r.ip}</td>,
        <td key="r" className="muted small">{r.reason}</td>,
      ];
    case "console":
      return [
        when,
        <td key="u">{r.username || <span className="faint">System</span>}</td>,
        <td key="a"><span className="tag">{r.action}</span></td>,
        <td key="t" className="mono small truncate" style={{ maxWidth: 240 }}>{r.target}</td>,
        <td key="i" className="mono small faint">{r.ip}</td>,
      ];
    case "alarm":
      return [
        when,
        <td key="d" className="mono">{deviceId(r.device_id)}</td>,
        <td key="t">{r.typ}</td>,
        <td key="i" className="muted small truncate" style={{ maxWidth: 320 }}>{r.info}</td>,
      ];
  }
}


/**
 * Whether this server keeps the recordings machines send it.
 *
 * Two halves, and both have to be on: the machine has to be told to record
 * (`allow-auto-record-incoming`, pushed by a settings profile) and this server
 * has to be willing to keep what it sends. Saying so here saves the puzzle of
 * an empty list with no explanation.
 */
function RecordingSetting() {
  const settings = useAsync(
    () => api.get<{ settings: Record<string, string> }>("/api/console/settings"), []);
  const on = settings.data?.settings?.store_recordings === "1";

  return (
    <div className="panel" style={{ marginBottom: "0.75rem" }}>
      <div className="panel__body">
        <label className="row" style={{ gap: "0.5rem", alignItems: "flex-start" }}>
          <input
            type="checkbox"
            checked={on}
            disabled={settings.loading}
            style={{ marginTop: "0.25rem" }}
            onChange={async (e) => {
              await api.post("/api/console/settings", {
                store_recordings: e.target.checked ? "1" : "0",
              });
              settings.reload();
            }}
          />
          <span>
            <strong>Keep recordings machines send</strong>
            <div className="small faint">
              A machine only records when it is told to, with{" "}
              <code className="mono">allow-auto-record-incoming</code> in a settings
              profile. This says whether the server keeps what arrives. Uploads are
              refused quietly while it is off, so a machine configured to record does
              not fill its own log with failures.
            </div>
          </span>
        </label>
      </div>
    </div>
  );
}
